import { createContext, useContext, useMemo } from "react";
import { LazyLoadRowProps } from "../../shared-components/LazyLoadTable";
import { ReportRow } from "../Table";
import type { ReportSummaryItemApi } from "../../api/Reports";
import { useLatestWorkflowStepRunQuery } from "../../api/ReportsApi";
import { reportFailed } from "../utils";
import { mergeLatestStepIntoReport } from "./inProcessMisconductV2026Rows";
import {
  buildInternalIqaV2026TableRow,
  type InternalIqaV2026RowOpts,
} from "./internalIqaMisconductV2026Rows";

export type InternalIqaMisconductV2026RowContextValue = InternalIqaV2026RowOpts;

const InternalIqaMisconductV2026RowContext =
  createContext<InternalIqaMisconductV2026RowContextValue | null>(null);

export function InternalIqaMisconductV2026RowProvider({
  value,
  children,
}: {
  value: InternalIqaMisconductV2026RowContextValue;
  children: React.ReactNode;
}) {
  return (
    <InternalIqaMisconductV2026RowContext.Provider value={value}>
      {children}
    </InternalIqaMisconductV2026RowContext.Provider>
  );
}

export function useInternalIqaMisconductV2026RowContext(): InternalIqaMisconductV2026RowContextValue {
  const ctx = useContext(InternalIqaMisconductV2026RowContext);
  if (!ctx) {
    throw new Error(
      "useInternalIqaMisconductV2026RowContext must be used within InternalIqaMisconductV2026RowProvider"
    );
  }
  return ctx;
}

const InternalIqaMisconductLazyRow = ({
  parentRow,
  index,
}: LazyLoadRowProps<ReportSummaryItemApi>): JSX.Element => {
  const rowCtx = useInternalIqaMisconductV2026RowContext();
  const isErrorRow = reportFailed(parentRow);

  const { data: latestStep, isLoading: stepQueryLoading } =
    useLatestWorkflowStepRunQuery(
      parentRow.workflow_run_uuid,
      !isErrorRow && Boolean(parentRow.workflow_run_uuid)
    );

  const stepLoading =
    !isErrorRow && stepQueryLoading && latestStep === undefined;

  const report = useMemo(
    () =>
      isErrorRow ? parentRow : mergeLatestStepIntoReport(parentRow, latestStep),
    [parentRow, isErrorRow, latestStep]
  );

  const tableRow = useMemo(
    () => buildInternalIqaV2026TableRow(report, { ...rowCtx, stepLoading }),
    [report, rowCtx, stepLoading]
  );

  return <ReportRow reportRow={tableRow} index={index} />;
};

export default InternalIqaMisconductLazyRow;
